import React from "react";
import ButtonMessage from "./ButtonMessage.jsx";
import DropdownMessage from "./DropdownMessage.jsx";
import ListViewMessage from "./ListViewMessage.jsx";
import QuickRepliesMessage from "./QuickRepliesMessage.jsx";
import SearchMessage from "./SearchMessage.jsx";
import TextMessageTemplate from "./TextMessageTemplate.jsx";
import TimeSlotTemplate from "./TimeSlotMessage.jsx";
import UserLiveChatMessage from "./UserLiveChatMessage.jsx";
import FormMessage from "./FormMessage.jsx";
import BotSurveyFeedbackMessage from "./BotSurveyFeedbackMessage.jsx";
import AdvancedFormMessage from "./AvancedFormMessage.jsx";
import LiveAgentMessage from "./LiveAgentMessage.jsx";
import TemplateNotMappedMessage from "./TemplateNotMappedMessage.jsx";
import PostLiveChatSurveyFrom from "./PostLiveChatSurveyForm.jsx";
import LoginMessage from "./LoginMessage.jsx";
import MindfulCallbackSucessMessage from "./MindfulCallbackSuccessMessage.jsx";

/**
 * Class that handles the mapping of all message types, both if passed through normally,
 * or as the rendering of chat history.
 */
export default class Messages extends React.Component {
    constructor(props) {
        super(props);

        this.isPreviousHistoryInteractable = false;
    }

    /**
     * Maps the chat history messages as received from the DDaS web socket service.
     *
     * We do need to know in this context if a previous message in history was of an interactable
     * type. This is due to the fact that as the customer is interacting live with the bot in their
     * initial chat, we have message types where what we send to the bot is not what we show as
     * the user's chat bubble. This means however, when history comes back from the bot, it returns
     * as the hidden payload instead of the nice customer message we initially displayed.
     *
     * A good example of this is the timeslot template where the payload we send to the bot initially
     * is in seconds since epoch for the API return to utilize to schedule a call. This payload then
     * is returned in seconds since as the history response. If we don't re-map this, then history will
     * simply print out the seconds. Instead each interactable message type has a mapper specifically
     * to print out the more friendly message. In the instance of timeslot, we send through the seconds,
     * convert it to a date and then a string such as "Call on DATE at TIME". That is sent back to this and
     * rendered out as a text message template.
     *
     * @param {Object} message The message object being mapped.
     * @param {Number} index The current index of the message collection being mapped.
     * @returns A mapped message or template to display in the chat window.
     */
    mapChatHistoryMessages(message, index) {
        /**
         * Checking against user should weed out some bot returns where certain templates can sometimes
         * be truly interactable but use the same format. A user response shouldn't be represented as anything
         * other than a text return in current state. This should let concurrent bot responses flow to the
         * general template mapping logic.
         */
        if (this.isPreviousHistoryInteractable && index > 0 && message.messageSender === 'user') {
            this.isPreviousHistoryInteractable = false;
            const previousMessage = this.props.messages[index - 1];
            const maskedTextReturn = this.mapInteractiveChatHistoryString(previousMessage, previousMessage.payload.payload, message.payload);
            return <TextMessageTemplate key={message} message={message} payload={maskedTextReturn} isFocusable='false'></TextMessageTemplate>
        }
        this.isPreviousHistoryInteractable = false;
        if ((message.messageSender === 'user' || message.messageSender === 'bot') && (typeof message.payload === 'string' || message.payload instanceof String)) {
            return <TextMessageTemplate key={message} message={message} payload={message.payload} isFocusable='false'></TextMessageTemplate>
        }
        if ((message.messageSender === 'user' || message.messageSender === 'bot') && (message.payload?.text && (typeof message.payload.text === 'string' || message.payload.text instanceof String))) {
            return <TextMessageTemplate key={message} message={message} payload={message.payload.text} isFocusable='false'></TextMessageTemplate>
        }
        if (message.payload.type === 'template') {
            return this.mapTemplates(message, message.payload.payload, true);
        }
    }

    /**
     * Maps the interactive message types using their respective static masking methods.
     */
    mapInteractiveChatHistoryString(message, payload, currentValue) {
        if (message?.payload?.type !== 'template') {
            return '';
        }

        switch(payload.template_type) {
            case 'button':
                return ButtonMessage.getMaskedHistory(payload, currentValue);
            case 'dropdown_template':
                return '';
            case 'listView':
                return ListViewMessage.getMaskedHistory(payload, currentValue);
            case 'quick_replies':
            case 'quick_replies_welcome':
                return QuickRepliesMessage.getMaskedHistory(payload, currentValue);
            case 'timeSlotTemplate':
                return TimeSlotTemplate.getMaskedHistory(currentValue);
            default:
                return '';
        }
    }

    /**
     * Maps the message sent through to the correct type.
     */
    mapMessages(message) {
        if (message.messageSender === 'user') {
            return <TextMessageTemplate key={message} message={message} payload={message.payload} isFocusable='false'></TextMessageTemplate>
        }

        const payload = message.payload;
        /**TODO: This should be set to an ErrorMessageTemplate once created. */
        if (payload.type === 'botKitUnreachable') {
            return <TextMessageTemplate key={message} message={message} payload={message.payload.message} isFocusable='false'></TextMessageTemplate>
        }
        if (payload.type === 'WebSocketUnavailable' || payload.type === 'WebsocketError') {
            return <TextMessageTemplate key={message} message={message} payload={'An error occurred trying to connect. Please try again later.'} isFocusable='false'></TextMessageTemplate>
        }
        if ((!payload.component?.type) || !payload.component.payload) {
            return <TextMessageTemplate key={message} message={message} payload={'There was an error.'} isFocusable='false'></TextMessageTemplate>
        }

        const component = payload.component;
        if ((component.type === 'text' || component.type === 'template') && (component && component.payload && component.payload.text)) {
            return <TextMessageTemplate key={message} message={message} payload={component.payload.text} isFocusable='false'></TextMessageTemplate>
        }

        /**
         * There is a new odd double nested text message returning which wasn't being handled properly and was falling
         * through to the template mapping logic which failed on template type checking.
         */
        if (component.payload?.component?.payload && (typeof component.payload.component.payload === 'string' || component.payload.component.payload instanceof String)) {
            return <TextMessageTemplate key={message} message={message} payload={component.payload.component.payload} isFocusable='false'></TextMessageTemplate>
        }

        if (component.type === 'template' && (typeof component.payload === 'string' || component.payload instanceof String)) {
            return <TextMessageTemplate key={message} message={message} payload={component.payload} isFocusable='false'></TextMessageTemplate>
        }

        if (component.type === 'template') {
            return this.mapTemplates(message, component.payload.payload);
        }
    }

    /**
     * Maps the template message to their respective concrete types.
     * @param {Object} message The message object constructed in the message handler of chatbot.jsx.
     * @param {Object} payload The payload of the message from the Kore response.
     * @param {Boolean} isMappingChatHistory A flag for when mapping chat history to indicate that the current
     * template being mapped is either interactable or not. Used the mask the chat history user response
     * if needed as described in mapChatHistoryMessages().
     * @returns A mapped template or a default message if the template type received isn't supported yet.
     */
    mapTemplates(message, payload, isMappingChatHistory = false) {
        switch (payload.template_type) {
            case 'advancedFormTemplate':
                return <AdvancedFormMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            case 'button':
                this.isPreviousHistoryInteractable = isMappingChatHistory;
                return <ButtonMessage
                    key={message}
                    message={message}
                    //Will have to search for other objects outside of the payload here apparently.
                    payload={payload}
                    isFocusable='true' />
            case 'botSurveyFeedbackTemplate':
                return <BotSurveyFeedbackMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            case 'dropdown_template':
                this.isPreviousHistoryInteractable = isMappingChatHistory;
                return <DropdownMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            case 'form_template':
                return <FormMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            case 'login':
                return <LoginMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            case 'listView':
                this.isPreviousHistoryInteractable = isMappingChatHistory;
                return <ListViewMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            case 'liveAgentMessage':
                message.messageSender = payload.agent_name;
                message.agentChat = true;
                return <LiveAgentMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='false' />
            case 'mindfulCallbackSuccessTemplate':
                return <MindfulCallbackSucessMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='false' />
            case 'postLiveChatSurveyTemplate':
                return <PostLiveChatSurveyFrom
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            case 'quick_replies':
            case 'quick_replies_welcome':
                this.isPreviousHistoryInteractable = isMappingChatHistory;
                return <QuickRepliesMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            case 'search':
                return <SearchMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            case 'timeSlotTemplate':
                this.isPreviousHistoryInteractable = isMappingChatHistory;
                return <TimeSlotTemplate
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            case 'userLiveChatTemplate':
                return <UserLiveChatMessage
                    key={message}
                    message={message}
                    payload={payload}
                    isFocusable='true' />
            default:
                return <TemplateNotMappedMessage
                    key={message}
                    message={message}
                    isFocusable='false' />
        }
    }

    render() {
        return (
            <div className='chat-window__messages'>
                {this.props.messages.map((message, index) => {
                    if (message.isChatHistory) {
                        return this.mapChatHistoryMessages(message, index);
                    }
                    else {
                        return this.mapMessages(message)
                    }
                })}
            </div>
        );
    }
}